import { closestCenter, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext } from '@payaca/dnd';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  ChangeEventHandler,
  FocusEventHandler,
  useContext,
  useId,
} from 'react';
import { FieldContext } from '../Field/Context';
export { arrayMove } from '@dnd-kit/sortable';

export type TOption = {
  id?: UniqueIdentifier;
  label: string | React.ReactNode;
  value: string;
  className?: string;
  disabled?: boolean;
};

export type TGroupFieldBaseProps<T extends boolean> = {
  value?: T extends true ? string[] : string;
  options: TOption[];
  horizontal?: boolean;
  cols?: number;
  multiple?: T;
  reverse?: boolean;
  onChange?: (value: T extends true ? string[] : string) => void;
  onTouch?: (fieldName: string) => void;
  emphasised?: boolean;
  hiddenInput?: boolean;
  sortable?: boolean;
  onDragEnd?: (event: DragEndEvent) => void;
  className?: string;
};

const GroupFieldBase = <T extends boolean>(props: TGroupFieldBaseProps<T>) => {
  const {
    options,
    value,
    multiple = false,
    horizontal: horizontalProp = false,
    onChange,
    onTouch,
    onDragEnd,
    sortable: sortableProp = false,
    className,
    cols: colsProps,
    ...rest
  } = props;
  const { name } = useContext(FieldContext);

  /**
   * Check if the options have unique ids when sortable is true
   */
  let sortable = false;
  const everyOptionHasId = options.every((option) => option.id !== undefined);
  if (sortableProp && everyOptionHasId) {
    sortable = true;
  } else if (sortableProp && !everyOptionHasId) {
    console.warn(
      'Sortable is enabled but some ids are missing for the options. Please provide unique ids for each option.'
    );
  }

  /**
   * Check if the horizontal prop is set to false when sortable is true
   */
  let horizontal = false;
  if (horizontalProp && !sortable) {
    horizontal = true;
  } else if (horizontalProp && sortable) {
    console.warn('GroupFieldBase cannot be horizontal when sortable is true.');
  }

  let cols: number | undefined = undefined;
  if (colsProps !== undefined && horizontal) {
    cols = colsProps;
  } else if (colsProps !== undefined && !horizontal) {
    console.warn('Cols prop is only applicable when horizontal is true.');
  }

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (multiple) {
      const _value = (value || []) as string[];

      if (e.target.checked) {
        onChange?.([..._value, e.target.value] as T extends true
          ? string[]
          : string);
      } else {
        onChange?.(
          _value.filter((v) => v !== e.target.value) as T extends true
            ? string[]
            : string
        );
      }
    } else {
      onChange?.(e.target.value as T extends true ? string[] : string);
    }
  };

  const handleTouch: FocusEventHandler<HTMLInputElement> = () => {
    onTouch?.(name || '');
  };

  return (
    <DndContext
      sortable
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <div
        className={clstx(
          'grid gap-2',
          horizontal && cols === undefined && 'sm:grid-flow-col',
          className
        )}
        style={{
          gridTemplateColumns: cols
            ? `repeat(${cols}, minmax(0, 1fr))`
            : undefined,
        }}
      >
        <SortableContext
          // @ts-expect-error option.id will be defined if sortable is true
          items={options}
          strategy={verticalListSortingStrategy}
          disabled={!sortable}
        >
          {options.map((option) => (
            <GroupFieldBaseInput
              key={option.value}
              option={option}
              onChange={handleChange}
              onFocus={handleTouch}
              multiple={multiple}
              checked={
                multiple
                  ? value?.includes(option.value) === true
                  : value === option.value
              }
              sortable={sortable}
              disabled={option.disabled}
              {...rest}
            />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
};

export type TGroupFieldBaseInputProps = {
  checked: boolean;
  option: TOption;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onFocus: FocusEventHandler<HTMLInputElement>;
  reverse?: boolean;
  multiple: boolean;
  emphasised?: boolean;
  hiddenInput?: boolean;
  sortable?: boolean;
  disabled?: boolean;
};

const GroupFieldBaseInput = (props: TGroupFieldBaseInputProps) => {
  const {
    option,
    reverse,
    multiple,
    checked,
    emphasised = false,
    hiddenInput = false,
    sortable = false,
    ...rest
  } = props;
  const { name } = useContext(FieldContext);
  const uId = useId();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id || uId, disabled: !sortable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <label
      ref={setNodeRef}
      htmlFor={uId}
      className={clstx(
        'flex w-full cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-base dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400',
        reverse && 'flex-row-reverse justify-between',
        option.className,
        emphasised && checked && 'border-blue-900 ring-1 ring-blue-900'
      )}
      style={style}
    >
      <input
        type={multiple ? 'checkbox' : 'radio'}
        name={name}
        className={clstx(
          multiple
            ? 'pl-form-checkbox cursor-pointer'
            : 'pl-form-radio cursor-pointer',
          hiddenInput && 'sr-only'
        )}
        id={uId}
        value={option.value}
        checked={checked}
        {...rest}
      />
      {typeof option.label === 'string' ? (
        <span className="text-base">{option.label}</span>
      ) : (
        option.label
      )}

      {sortable && (
        <div
          className={clstx(
            'inline-flex p-3 -m-3',
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
            reverse ? 'mr-auto pr-0' : 'ml-auto pl-0'
          )}
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
        >
          <UntitledIcon name="draggable" className="w-5 h-5" />
        </div>
      )}
    </label>
  );
};

export default GroupFieldBase;
